import React, { useEffect, useState } from "react"
import {
    Button,
    CircularProgress,
    Dialog,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material"
import { Close } from "@mui/icons-material"

const buttonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } }

const todayInput = () => new Date().toISOString().split("T")[0]

/**
 * Create or edit a registration link. `link` is the link being edited, or
 * null to create one. Submits { title, classRef, expiryDate }.
 */
export default function LinkFormDialog({ open, link, classes, saving, onClose, onSubmit }) {
    const [title, setTitle] = useState("")
    const [classRef, setClassRef] = useState("")
    const [expiryDate, setExpiryDate] = useState("")
    const [touched, setTouched] = useState(false)

    useEffect(() => {
        if (!open) return
        setTitle(link?.title || "")
        setClassRef(link?.class_ref || "")
        setExpiryDate(link?.expired ? String(link.expired).split("T")[0] : "")
        setTouched(false)
    }, [open, link])

    const isEdit = Boolean(link)
    const expiryInPast = Boolean(expiryDate) && expiryDate < todayInput()
    const errors = {
        title: !title.trim() && "Give the link a title",
        classRef: !classRef && "Choose a class",
        expiryDate: !expiryDate ? "Choose an expiry date" : expiryInPast && "Pick today or a later date"
    }
    const valid = !errors.title && !errors.classRef && !errors.expiryDate

    const submit = (e) => {
        e.preventDefault()
        setTouched(true)
        if (!valid || saving) return
        onSubmit({ title: title.trim(), classRef, expiryDate })
    }

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ component: "form", onSubmit: submit, noValidate: true, sx: { borderRadius: "18px" } }}
        >
            <header className="flex items-start justify-between gap-3 px-6 pb-2 pt-5">
                <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                        {isEdit ? "Edit registration link" : "New registration link"}
                    </h2>
                    <p className="mt-0.5 text-sm text-slate-500">
                        Students who open the link can register for the class until it expires.
                    </p>
                </div>
                <IconButton aria-label="Close" onClick={onClose} disabled={saving} sx={{ mr: -1, mt: -0.5 }}>
                    <Close />
                </IconButton>
            </header>

            <div className="flex flex-col gap-4 px-6 py-4">
                <TextField
                    label="Title"
                    placeholder="e.g. Admissions 2026–27"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    error={touched && Boolean(errors.title)}
                    helperText={touched && errors.title}
                    autoFocus
                    fullWidth
                    sx={fieldSx}
                />
                <FormControl fullWidth error={touched && Boolean(errors.classRef)} sx={fieldSx}>
                    <InputLabel id="link-class-label">Class</InputLabel>
                    <Select
                        labelId="link-class-label"
                        label="Class"
                        value={classRef}
                        onChange={(e) => setClassRef(e.target.value)}
                    >
                        {classes.map((item) => (
                            <MenuItem key={item.id} value={item.id}>{item.class_name}</MenuItem>
                        ))}
                    </Select>
                    {touched && errors.classRef && (
                        <p className="ml-3.5 mt-1 text-xs text-red-600">{errors.classRef}</p>
                    )}
                </FormControl>
                <TextField
                    type="date"
                    label="Expires on"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: todayInput() }}
                    error={touched && Boolean(errors.expiryDate)}
                    helperText={(touched && errors.expiryDate) || "The link stops accepting registrations after this date."}
                    fullWidth
                    sx={fieldSx}
                />
            </div>

            <footer className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
                <Button onClick={onClose} disabled={saving} color="inherit" sx={buttonSx}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ ...buttonSx, px: 2.5 }}
                >
                    {isEdit ? "Save changes" : "Create link"}
                </Button>
            </footer>
        </Dialog>
    )
}
